import React, { useMemo } from 'react';
import { Progress, Spin } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import type { Dayjs } from 'dayjs';
import { Empty } from '@maxtropy/components';
import { apiV2ReportDataUsageAndCostPost, apiV2ReportDataAbnormalEventsPost } from '@maxtropy/device-customer-apis-v2';
import { keepTwoOrLine, keepTwoAndWanOrLine, keepPercentOrLine, keepZeroOrLine } from '@/utils/utils';
import { ReportType, ENERGY_TYPES, getReportTimeRange } from '../../const';
import CardBox from '../CardBox';
import spinCircleWrapper from '../../imgs/spin_circle_wrapper.png';
import totalCost from '../../imgs/total_cost.png';
import spinError from '../../imgs/spin_error.png';
import elecIcon from '../../imgs/elec_icon.png';
import waterIcon from '../../imgs/water_icon.png';
import gasIcon from '../../imgs/gas_icon.png';
import iconHighAlarm from '../../imgs/icon_high_alarm.png';
import iconWatergasAlarm from '../../imgs/icon_watergas_alarm.png';
import iconLongwater from '../../imgs/icon_longwater.png';
import iconWaterPipe from '../../imgs/icon_water_pipe.png';
import iconValveLeak from '../../imgs/icon_valve_leak.png';
import iconErrorStatics from '../../imgs/icon_error_statics.png';
import styles from './index.module.scss';

interface ReportOverviewProps {
  reportType: ReportType;
  selectedDate: Dayjs;
}

const ReportOverview: React.FC<ReportOverviewProps> = ({ reportType, selectedDate }) => {
  // 构建请求参数
  const requestParams = useMemo(() => getReportTimeRange(reportType, selectedDate), [reportType, selectedDate]);

  // 调用报告总览 API
  const { data: apiData, loading: costLoading } = useRequest(() => apiV2ReportDataUsageAndCostPost(requestParams), {
    refreshDeps: [requestParams],
  });

  // 调用异常事件 API（参数与用能成本接口相同）
  const { data: abnormalData, loading: abnormalLoading } = useRequest(
    () => apiV2ReportDataAbnormalEventsPost(requestParams),
    {
      refreshDeps: [requestParams],
    }
  );

  const loading = costLoading || abnormalLoading;

  // 将 API 数据映射为能源类型卡片数据
  const energyTypeCards = useMemo(() => {
    if (!apiData?.mediumCostList) return [];

    // 光伏发电卡片（固定在最前面）
    const pvCard = {
      key: 'pv',
      name: '光伏发电',
      icon: elecIcon,
      unit: 'kWh',
      value: apiData.pvPower,
      cost: apiData.pv,
      planUsage: undefined,
      rate: undefined,
    };

    // 其他介质卡片
    const mediumCards = apiData.mediumCostList.map(item => {
      // 根据能源介质名称匹配图标
      const matchedType = ENERGY_TYPES.find(
        et => et.name === item.energyMediumName || et.key === item.energyMediumName
      );

      return {
        key: item.energyMediumId?.toString() || item.energyMediumName || '',
        name: item.energyMediumName || '',
        icon: matchedType?.icon || elecIcon,
        unit: item.unitName || 'kWh', // 使用接口返回的单位字段
        value: item.usage,
        cost: item.cost,
        planUsage: item.planUsage,
        rate: item.rate,
      };
    });

    return [pvCard, ...mediumCards];
  }, [apiData]);

  // 异常详情数据（从 API 获取）
  const anomalyDetails = useMemo(() => {
    if (!abnormalData) return [];

    return [
      {
        key: 'electricAlarm',
        title: '电力高级报警',
        value: abnormalData.electricHighLevelAlarm ?? 0,
        icon: iconHighAlarm,
      },
      {
        key: 'waterGasAlarm',
        title: '水气高级报警',
        value: abnormalData.waterGasHighLevelAlarm ?? 0,
        icon: iconWatergasAlarm,
      },
      { key: 'longFlow', title: '长流水异常', value: abnormalData.longWaterFlowAbnormal ?? 0, icon: iconLongwater },
      {
        key: 'pipeDeviation',
        title: '水管网偏差',
        value: abnormalData.waterNetworkDeviation ?? 0,
        icon: iconWaterPipe,
      },
      { key: 'valveLeak', title: '分管阀门泄露', value: abnormalData.branchValveLeakage ?? 0, icon: iconValveLeak },
    ];
  }, [abnormalData]);

  return (
    <CardBox title="报告总览">
      <Spin spinning={loading}>
        {/* 核心指标卡片 - 带背景图，宽度按比例分配 */}
        <div className={styles.metric_row}>
          {/* 总成本 - 25% */}
          <div className={styles.metric_card_small}>
            <div className={styles.icon_wrapper}>
              <img className={styles.spin_icon} src={spinCircleWrapper} alt="" />
              <img className={styles.static_icon} src={totalCost} alt="" />
            </div>
            <div className={styles.card_content}>
              <span className={styles.metric_label}>总成本</span>
              <span className={styles.metric_value}>{keepTwoAndWanOrLine(apiData?.totalCost)}元</span>
            </div>
          </div>

          {/* 异常事件 - 25% */}
          <div
            className={styles.metric_card_small}
            style={{ cursor: 'pointer' }}
            onClick={() => window.open('/device/alarm/record', '_blank')}
          >
            <div className={styles.icon_wrapper}>
              <img className={styles.spin_icon} src={spinError} alt="" />
              <WarningFilled className={styles.static_icon_warning} />
            </div>
            <div className={styles.card_content}>
              <span className={styles.metric_label}>异常报警</span>
              <span className={styles.metric_value_warning}>
                {keepZeroOrLine(abnormalData?.totalAbnormalEvents)} 条
              </span>
            </div>
          </div>

          {/* 用能成本占比计划 + 计划总成本 - 50% */}
          {reportType === ReportType.MONTHLY && (
            <div className={styles.metric_card_large}>
              <div className={styles.icon_wrapper}>
                <Progress
                  type="circle"
                  percent={(apiData?.rate ?? 0) * 100}
                  strokeColor="#00ADFF"
                  trailColor="rgba(0, 173, 255, 0.1)"
                  strokeWidth={8}
                  showInfo={false}
                  width={56}
                />
              </div>
              <div className={styles.card_content_double}>
                <div className={styles.card_content}>
                  <span className={styles.metric_label}>用能成本占比计划</span>
                  <span className={styles.metric_value_green}>{keepPercentOrLine(apiData?.rate)}%</span>
                </div>
                <div className={styles.card_content}>
                  <span className={styles.metric_label}>计划总成本</span>
                  <span className={styles.metric_value}>{keepTwoAndWanOrLine(apiData?.planCharges)}元</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 能源类型卡片 */}
        <div className={styles.energy_types_wrapper}>
          {energyTypeCards.length === 0 ? (
            <div className={styles.empty_wrapper}>
              <Empty />
            </div>
          ) : (
            <div className={styles.energy_cards_row}>
              {energyTypeCards.map(et => (
                <div key={et.key} className={styles.energy_card}>
                  <img className={styles.energy_icon} src={et.icon} alt={et.name} />
                  <div className={styles.energy_name}>
                    {et.name} ({et.unit})
                  </div>
                  <div className={styles.energy_item}>
                    <span className={styles.energy_item_label}>实际：</span>
                    <span className={styles.energy_value}>{keepTwoAndWanOrLine(et.value)}</span>
                  </div>
                  {reportType === ReportType.MONTHLY && et.key !== 'pv' && (
                    <>
                      <div className={styles.energy_item}>
                        <span className={styles.energy_item_label}>计划：</span>
                        <span className={styles.energy_plan_value}>{keepTwoAndWanOrLine(et.planUsage)}</span>
                      </div>
                      <div className={styles.energy_item}>
                        <span className={styles.energy_item_label}>占比计划：</span>
                        <span className={styles.energy_percent}>{keepPercentOrLine(et.rate)}%</span>
                      </div>
                    </>
                  )}
                  <div className={styles.energy_divider} />
                  <div className={styles.energy_item}>
                    <span className={styles.energy_item_label}>{et.key === 'pv' ? '节约成本：' : '成本：'}</span>
                    <span className={styles.energy_cost}>{keepTwoAndWanOrLine(et.cost)}元</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 异常统计 */}
        <div className={styles.anomaly_strip}>
          <div className={styles.anomaly_strip_header}>
            <img className={styles.anomaly_strip_icon} src={iconErrorStatics} alt="" />
            <span>异常统计</span>
          </div>
          {/* 异常详情五大块 */}
          {anomalyDetails.length === 0 ? (
            <div className={styles.empty_wrapper}>
              <Empty />
            </div>
          ) : (
            <div className={styles.anomaly_details}>
              {anomalyDetails.map(item => (
                <div key={item.key} className={styles.anomaly_detail_item}>
                  <div className={styles.anomaly_detail_icon}>
                    <img src={item.icon} alt={item.title} />
                  </div>
                  <div className={styles.anomaly_detail_right}>
                    <span className={styles.anomaly_detail_title}>{item.title}</span>
                    <span className={styles.anomaly_detail_value}>{keepZeroOrLine(item.value)} 条</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Spin>
    </CardBox>
  );
};

export default ReportOverview;
